import React, { useCallback } from 'react';
import {
  Actions,
  ModelScopeFactory,
} from '@cotera/client/v0/actions/framework';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { useUrlAdapter } from '@cotera/client/app/etc/url-param-watchable';
import { useArtifactPanel } from '@cotera/client/v0/chat/artifact-panel-context';
import { WorkflowModel } from './workflow.model';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';
import {
  ArtifactDrillProvider,
  type DrillTarget,
} from './workflow-drill-context';
import { WorkflowDrillDetail } from './workflow-drill-detail';

/**
 * Hosts a workflow node/trigger config form inside the chat artifact panel
 * (TJ's "node = artifact" model). Recreates the {@link WorkflowModel} scope for
 * the workflow so the existing drawers' mutations (save node, bind trigger, …)
 * work, then renders the same {@link WorkflowDrillDetail} the in-canvas stage
 * used. Closing focuses the workflow artifact again (back to the canvas).
 */
const WorkflowNodeArtifactResolved: React.FC<{
  workflowId: string;
  artifactId: string;
  target: NonNullable<DrillTarget>;
  orgId: string;
  userId: string;
}> = ({ workflowId, artifactId, target, orgId, userId }) => {
  const client = useTenantedClient();
  const watchablesFactory = useWatchablesFactory();
  const urlAdapter = useUrlAdapter();
  const artifactPanel = useArtifactPanel();

  const createModels = useCallback(
    () => [
      new WorkflowModel({
        workflowId,
        orgId,
        userId,
        isNew: false,
        client,
        watchablesFactory,
        urlAdapter,
      }),
    ],
    [workflowId, orgId, userId, client, watchablesFactory, urlAdapter]
  );

  const close = useCallback(() => {
    // Dismiss this transient config tab and return to the workflow canvas (its
    // artifactId is the workflowId).
    artifactPanel?.closeArtifact(artifactId);
    artifactPanel?.focusArtifact(workflowId);
  }, [artifactPanel, artifactId, workflowId]);

  return (
    <Actions actions={[]}>
      <ModelScopeFactory
        createModels={createModels}
        deps={[workflowId, urlAdapter]}
      >
        <ArtifactDrillProvider target={target} onClose={close}>
          <div className="relative h-full w-full overflow-hidden bg-background">
            <WorkflowDrillDetail target={target} />
          </div>
        </ArtifactDrillProvider>
      </ModelScopeFactory>
    </Actions>
  );
};

export const WorkflowNodeArtifact: React.FC<{
  workflowId: string;
  artifactId: string;
  target: DrillTarget;
}> = ({ workflowId, artifactId, target }) => {
  const orgId = useWhoami((s) => s.org?.id);
  const userId = useWhoami((s) => s.whoami?.id);
  if (orgId === undefined || userId === undefined || target === null) {
    return null;
  }
  return (
    <WorkflowNodeArtifactResolved
      workflowId={workflowId}
      artifactId={artifactId}
      target={target}
      orgId={orgId}
      userId={userId}
    />
  );
};
